import { blueSecondaryLink } from 'components/Globals/Base/Link/LinkStyles';
import styled from 'styled-components';
import { breakpoint, color, font } from 'styles/globals';
import { getColor, type Theme } from 'styles/theme';
import { ctaCaption } from 'styles/paragraphs';
import { yesNoIcons } from 'styles/icons';

const generateFeaturedRows = (size: number) => {
  let selectors = `&:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) + tr`;
  for (let i = 1; i < size - 2; i += 1) {
    selectors += `, &:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) ${'+ tr'.repeat(i)}`;
  }
  return selectors;
};

const generateLastFeaturedRow = (size: number) => {
  const selectors = `&:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) ${'+ tr'.repeat((size < 2 ? 2 : size) - 2)}`;
  return selectors;
};

export const ResponsiveTableWrapper = styled.div`
  width: 100%;
  margin: 24px 0;
`;

const ResponsiveTable = styled.table<{ $colNumber?: number; theme: Theme }>`
  border-collapse: collapse;
  font-family: ${font.graphikCond};
  text-align: left;
  width: 100%;
  ${yesNoIcons};

  mark {
    display: block;
    font-size: 12px;
    line-height: 18px;
    font-weight: 500;
    color: ${getColor('textLink')};

    & + br {
      display: none;
    }
  }

  ul, ol {
    margin: 0;
    }
    
  .last-cell {
    & > a:first-child {
      ${blueSecondaryLink};
      display: flex;
      align-items: center;
      gap: 4px;
      font-weight: 600;
      font-size: 16px;
      text-wrap: nowrap;

      &:not([href*="fortune.com"]):not([href^="#"]):after,
      &[href*="fortune.com/recommends/redirect"]:after {
        content: url("/icons/OpenInNewTab.svg");
        height: 19px;
        width: 19px;
      }
    }
      
    &:has(a:first-child) {
      ${ctaCaption};
      text-align: left;
      text-wrap: nowrap;
      line-height: 75%;
    }
  }
`;

export const ResponsiveTableDesktop = styled(ResponsiveTable)`
  display: none;

  tr {
    font-size: 16px;
  }

  th, td {
    padding: 16px 16px;
    position: relative;

    &:nth-child(1) {
      padding-left: 24px;
    }

    &:last-child {
      padding-right: 24px;
    }

    &:not(:last-child):after {
        content:"";
        border-right: 1px solid ${getColor('borderSecondary')};
        position: absolute;
        top: 8px;
        bottom: 8px;
        right: 0px;
    }
  }

  tbody {
    th {
      font-weight: 400;
    }

    tr:not(:last-child) {
      border-bottom: 2px solid ${getColor('borderSecondary')};
      
      &:has(+ tr .has-light-green-cyan-color, 
        +tr .has-vivid-green-cyan-color) {
        border-bottom: none;
      }

      &:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) {
          background-color: ${color.lightBlue};
          border: 2px solid ${getColor('textLink')};
      } 
    }

    tr:nth-child(1) {
      border-top: 2px solid ${color.recommendsDarkGreen};
      &:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) {
        background-color: ${color.lightBlue};
        border: 2px solid ${getColor('textLink')};
      }
    }

    & > tr:nth-of-type(2n) {
      background-color: ${getColor('surfaceSecondary')};
    }
  }

  @media (${breakpoint.mdMin}) {
    display: table;
  }
`;

export const ResponsiveTableMobile = styled(ResponsiveTable)`
  display: table;

  thead {
    
    tr > th {
      font-size: 16px;
      font-weight: 600;
      line-height: 19px;
      letter-spacing: 0.5px;
      padding: 8px 0;
    }
  }
    
  tbody {
    &:before {
      content: "@";
      display: block;
      line-height: 0px;
      visibility: hidden;
    }
    
    th, td {
      padding: 8px;
    }

    th.header {
      font-weight: 400;
    }

    & > tr {
      font-size: 14px;

      &:not(:last-child) {
        border-bottom: 1px solid ${getColor('borderSecondary')};
      } 
    }

    & > tr:nth-of-type(${({ $colNumber }) => ($colNumber as number) - 1}n+1) {
      background-color: ${getColor('surfaceSecondary')};
      border-bottom: none;
      font-size: 16px;
      letter-spacing: 0.5px;

      &:has(.has-light-green-cyan-color, .has-vivid-green-cyan-color) {
        background-color: ${color.lightBlue};
        border-right: 2px solid ${getColor('textLink')};
        border-left: 2px solid ${getColor('textLink')};
        border-top: 2px solid ${getColor('textLink')};
      }

      ${({ $colNumber = 5 }) => generateFeaturedRows($colNumber)} {
      background-color: ${color.lightBlue};
      border-right: 2px solid ${getColor('textLink')};
      border-left: 2px solid ${getColor('textLink')};
    }

    ${({ $colNumber = 5 }) => generateLastFeaturedRow($colNumber)} {
      background-color: ${color.lightBlue};
      border-right: 2px solid ${getColor('textLink')};
      border-left: 2px solid ${getColor('textLink')};
      border-bottom: 2px solid ${getColor('textLink')};
    }
    }

  }

  @media (${breakpoint.mdMin}) {
    display: none;
  }
`;
